import { Autocomplete, Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { useEffect, useState } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

import { SnackbarTypeSeverity } from '../../../constants/types';
import { getServicesInvestigationService } from '../../../services/agenda';
import { addServiceToRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canModifyRequestServices, requestTerminologyKey } from './requestModification';
import { IRequest, IServiceInvestigation, ServiceType } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface AddServiceActionCoreProps extends LocalizeContextProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    requestServiceType?: ServiceType;
    availableServices: IServiceInvestigation[];
    onAdd: (servicesInvestigationId: number[], reason: string) => void | Promise<boolean>;
}

// Presentational core: when the user may modify services, offer an "Add service"
// button that opens the shared reason modal carrying a multi-select picker over
// the investigation's catalogue. Renders nothing when the policy disallows it.
export const AddServiceActionCore: React.FC<AddServiceActionCoreProps> = ({ request, permissions, loading, requestServiceType, availableServices, onAdd, translate }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<IServiceInvestigation[]>([]);

    if (!canModifyRequestServices(request, permissions)) {
        return null;
    }

    const close = () => { setOpen(false); setSelected([]); };

    return (
        <>
            <ReasonModal
                open={open}
                closeModal={close}
                onConfirm={(reason) => onAdd(selected.map((s) => s.id), reason)}
                loading={loading}
                titleId={requestTerminologyKey(requestServiceType, 'add_section')}
                bodyId={requestTerminologyKey(requestServiceType, 'add_modal')}
                reasonLabelId="pages.hospital.services.request.add_service.reason_label"
                reasonTestId="add-reason"
                confirmDisabled={selected.length === 0}
            >
                <Autocomplete
                    multiple
                    options={availableServices}
                    getOptionLabel={(option) => option.description}
                    value={selected}
                    onChange={(_event, newValue) => setSelected(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={translate(requestTerminologyKey(requestServiceType, 'select_label')) as string}
                            inputProps={{ ...params.inputProps, 'data-testid': 'add-service-select' }}
                        />
                    )}
                />
            </ReasonModal>
            {/* SHER-909: one control, not a heading AND a button. The panel above carries the
                only heading in this section; the add affordance says what it does on the
                button itself, next to the services it adds to.
                SHER-1062: the label now lives inside the button, as in the reporter's mockup,
                instead of a round "+" with a caption beside it. */}
            <Button variant="outlined" startIcon={<AddIcon />} data-testid="add-service" disabled={loading} onClick={() => setOpen(true)}>
                <Translate id={requestTerminologyKey(requestServiceType, 'add_section')} />
            </Button>
        </>
    );
};

export const AddServiceActionCoreLocalized = withLocalize(AddServiceActionCore);

interface AddServiceActionProps {
    request: IRequest;
    uuidInvestigation: string;
    requestServiceType: ServiceType;
    permissions: string[];
    onChanged: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: loads the investigation's service catalogue for this request's type
// (via the canonical service helper) and wires the add-service call to the core.
const AddServiceAction: React.FC<AddServiceActionProps> = ({ request, uuidInvestigation, requestServiceType, permissions, onChanged, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onChanged);
    const [availableServices, setAvailableServices] = useState<IServiceInvestigation[]>([]);

    useEffect(() => {
        if (!canModifyRequestServices(request, permissions)) {
            return;
        }
        getServicesInvestigationService(uuidInvestigation, requestServiceType)
            .then((response) => setAvailableServices(response.servicesInvestigation || []))
            .catch(() => setAvailableServices([]));
    }, [uuidInvestigation, requestServiceType]);

    const handleAdd = (servicesInvestigationId: number[], reason: string) =>
        run(() => addServiceToRequestService(uuidInvestigation, request.id, servicesInvestigationId, reason), 'pages.hospital.services.request.add_service.success');

    return (
        <AddServiceActionCoreLocalized
            request={request}
            permissions={permissions}
            loading={loading}
            requestServiceType={requestServiceType}
            availableServices={availableServices}
            onAdd={handleAdd}
        />
    );
};

export default AddServiceAction;
